import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable, type DropResult } from "@hello-pangea/dnd";
import { GripVertical, Pencil, Plus, Trash2 } from "lucide-react";
import { createPortal } from "react-dom";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "components/ui/accordion";
import { Button } from "components/ui/button";
import { Card, CardContent, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { PageLoadingState } from "components/layout/PageLoadingState";
import { Textarea } from "components/ui/textarea";
import { useToast } from "components/ui/use-toast";
import { useSupportEmail } from "hooks/useSupportEmail";
import { useAuth } from "contexts/AuthContext";
import { useEventTracking } from "hooks/useEventTracking";
import { faq, type FaqItem } from "services/api/faqClient";

type EditableFaqItem = {
  clientId: string;
  id?: number;
  title: string;
  content: string;
  display_order: number;
};

const toEditableItem = (item: FaqItem, index: number): EditableFaqItem => ({
  clientId: `faq-${item.id}`,
  id: item.id,
  title: item.title,
  content: item.content,
  display_order: item.display_order ?? index,
});

const normalizeOrder = (items: EditableFaqItem[]): EditableFaqItem[] =>
  items.map((item, index) => ({
    ...item,
    display_order: index,
  }));

export const Faq: React.FC = () => {
  const supportEmail = useSupportEmail();
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_staff);
  const { addToast } = useToast();
  const { track } = useEventTracking();
  const draftIdCounter = useRef(0);

  const [faqItems, setFaqItems] = useState<EditableFaqItem[]>([]);
  const [draftItems, setDraftItems] = useState<EditableFaqItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const dndPortalTarget = typeof document !== "undefined" ? document.body : null;

  const createDraftId = useCallback(() => {
    draftIdCounter.current += 1;
    return `new-faq-${draftIdCounter.current}`;
  }, []);

  const cloneItems = useCallback(
    (items: EditableFaqItem[]) =>
      items.map((item) => ({
        ...item,
      })),
    [],
  );

  const loadFaqItems = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await faq.list();
      const data = Array.isArray(response.data) ? response.data : [];
      const normalized = data
        .sort((a, b) => a.display_order - b.display_order || a.id - b.id)
        .map(toEditableItem)
        .map((item, index) => ({
          ...item,
          display_order: index,
        }));
      setFaqItems(normalized);
      setDraftItems(cloneItems(normalized));
    } catch {
      setLoadError("Unable to load FAQs right now.");
    } finally {
      setIsLoading(false);
    }
  }, [cloneItems]);

  useEffect(() => {
    void loadFaqItems();
  }, [loadFaqItems]);

  const publicFaqItems = useMemo(
    () => faqItems.slice().sort((a, b) => a.display_order - b.display_order),
    [faqItems],
  );

  const hasValidationError = useMemo(
    () => draftItems.some((item) => !item.title.trim() || !item.content.trim()),
    [draftItems],
  );

  const hasDraftChanges = useMemo(() => {
    const normalize = (items: EditableFaqItem[]) =>
      items.map((item, index) => ({
        id: item.id ?? null,
        title: item.title.trim(),
        content: item.content.trim(),
        display_order: index,
      }));
    return JSON.stringify(normalize(faqItems)) !== JSON.stringify(normalize(draftItems));
  }, [faqItems, draftItems]);

  const handleAccordionChange = (value: string) => {
    if (!value) {
      return;
    }
    const openedItem = publicFaqItems.find((item) => String(item.id) === value);
    if (!openedItem) {
      return;
    }
    track("button_click", { action: "FAQ card clicked", card: openedItem.title });
  };

  const handleEnableEditMode = () => {
    setDraftItems(cloneItems(faqItems));
    setSaveError(null);
    setIsEditMode(true);
  };

  const handleCancelChanges = () => {
    setDraftItems(cloneItems(faqItems));
    setSaveError(null);
    setIsEditMode(false);
  };

  const handleAddItem = () => {
    setDraftItems((prev) => [
      ...prev,
      {
        clientId: createDraftId(),
        title: "",
        content: "",
        display_order: prev.length,
      },
    ]);
  };

  const handleDeleteDraftItem = (clientId: string) => {
    setDraftItems((prev) => normalizeOrder(prev.filter((item) => item.clientId !== clientId)));
  };

  const handleDraftFieldChange = (
    clientId: string,
    field: "title" | "content",
    value: string,
  ) => {
    setDraftItems((prev) =>
      prev.map((item) => (item.clientId === clientId ? { ...item, [field]: value } : item)),
    );
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;
    if (sourceIndex === destinationIndex) {
      return;
    }
    setDraftItems((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(destinationIndex, 0, moved);
      return normalizeOrder(next);
    });
  };

  const handleSaveChanges = async () => {
    if (!isAdmin || hasValidationError || !hasDraftChanges) {
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const payload = draftItems.map((item, index) => ({
        id: item.id,
        title: item.title.trim(),
        content: item.content.trim(),
        display_order: index,
      }));
      const response = await faq.admin.bulkSave(payload);
      const data = Array.isArray(response.data) ? response.data : [];
      const normalized = data
        .sort((a, b) => a.display_order - b.display_order || a.id - b.id)
        .map(toEditableItem)
        .map((item, index) => ({
          ...item,
          display_order: index,
        }));
      setFaqItems(normalized);
      setDraftItems(cloneItems(normalized));
      setIsEditMode(false);
      addToast({ title: "FAQs updated", description: "Your changes are now live." });
    } catch {
      setSaveError("Unable to save FAQ changes.");
      addToast({
        title: "Unable to save FAQs",
        description: "Please retry.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    isLoading ? (
      <PageLoadingState />
    ) : (
    <div className="relative flex flex-col items-center">
      <div className="mt-5 flex w-full min-w-0 flex-col items-center gap-10">
        <Card
          maxWidth="none"
          variant="glass"
          padding="default"
          disableAnimation
          className="sm:p-10"
        >
          <div className="flex items-center justify-between gap-3 pb-5">
            <CardTitle title="FAQ" className="pb-0" />
            {isAdmin && !isLoading && !isEditMode && (
              <Button variant="secondary" size="auto" onClick={handleEnableEditMode}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
            )}
          </div>

          <CardContent padding="none">
            {loadError ? (
              <div className="flex flex-col items-start gap-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <p>{loadError}</p>
                <Button size="auto" variant="secondary" onClick={() => void loadFaqItems()}>
                  Retry
                </Button>
              </div>
            ) : isEditMode ? (
              <div className="w-full space-y-4">
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="faq-editor-list">
                    {(dropProvided) => (
                      <div
                        ref={dropProvided.innerRef}
                        {...dropProvided.droppableProps}
                        className="w-full space-y-3"
                      >
                        {draftItems.map((item, index) => (
                          <Draggable key={item.clientId} draggableId={item.clientId} index={index}>
                            {(dragProvided, dragSnapshot) => {
                              const draggableContent = (
                                <div
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  style={{
                                    ...dragProvided.draggableProps.style,
                                    ...(dragSnapshot.isDragging ? { zIndex: 2000 } : {}),
                                  }}
                                  className={`w-full rounded-2xl border bg-white/70 p-4 ${
                                    dragSnapshot.isDragging ? "shadow-lg" : ""
                                  }`}
                                >
                                  <div className="mb-3 flex items-center justify-between gap-2">
                                    <button
                                      type="button"
                                      className="inline-flex cursor-grab items-center rounded-md p-1 text-foundation-base-darkbase-dark-500"
                                      {...dragProvided.dragHandleProps}
                                      aria-label="Drag to reorder FAQ"
                                    >
                                      <GripVertical className="h-4 w-4" />
                                    </button>
                                    <Button
                                      type="button"
                                      size="icon"
                                      variant="ghost"
                                      onClick={() => handleDeleteDraftItem(item.clientId)}
                                      aria-label="Delete FAQ item"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                  <div className="space-y-3">
                                    <Input
                                      value={item.title}
                                      placeholder="Question"
                                      onChange={(event) =>
                                        handleDraftFieldChange(item.clientId, "title", event.target.value)
                                      }
                                    />
                                    <Textarea
                                      value={item.content}
                                      placeholder="Answer"
                                      onChange={(event) =>
                                        handleDraftFieldChange(item.clientId, "content", event.target.value)
                                      }
                                      className="min-h-[100px]"
                                    />
                                  </div>
                                </div>
                              );

                              if (dragSnapshot.isDragging && dndPortalTarget) {
                                return createPortal(draggableContent, dndPortalTarget);
                              }
                              return draggableContent;
                            }}
                          </Draggable>
                        ))}
                        {dropProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

                <div className="flex flex-wrap items-center gap-3">
                  <Button type="button" variant="secondary" size="auto" onClick={handleAddItem}>
                    <Plus className="h-4 w-4" />
                    Add FAQ
                  </Button>
                  <Button
                    type="button"
                    size="auto"
                    onClick={() => void handleSaveChanges()}
                    disabled={isSaving || hasValidationError || !hasDraftChanges}
                  >
                    {isSaving ? "Saving..." : "Save changes"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="auto"
                    onClick={handleCancelChanges}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                </div>

                {hasValidationError && (
                  <p className="text-sm text-red-700">Each FAQ item needs both a question and an answer.</p>
                )}
                {saveError && <p className="text-sm text-red-700">{saveError}</p>}
              </div>
            ) : publicFaqItems.length === 0 ? (
              <p className="text-sm text-foundation-base-darkbase-dark-500">No FAQs available yet.</p>
            ) : (
              <Accordion
                type="single"
                collapsible
                className="w-full space-y-6"
                onValueChange={handleAccordionChange}
              >
                {publicFaqItems.map((item) => (
                  <AccordionItem key={item.clientId} value={String(item.id)}>
                    <AccordionTrigger>
                      <span className="flex-1 text-left type-body-m-semibold text-foundation-base-darkbase-dark-500 text-sm tracking-normal leading-5 sm:text-[length:var(--body-m-semibold-font-size)] sm:tracking-[var(--body-m-semibold-letter-spacing)] sm:leading-[var(--body-m-semibold-line-height)]">
                        {item.title}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="type-body-m text-foundation-base-darkbase-dark-500 text-sm tracking-normal leading-5 sm:text-[length:var(--body-m-font-size)] sm:tracking-[var(--body-m-letter-spacing)] sm:leading-[var(--body-m-line-height)]">
                        {item.content}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </CardContent>
        </Card>

        <Card
          variant="glass"
          maxWidth="none"
          padding="default"
          className="flex w-full flex-col items-start gap-6 translate-y-[-1rem] sm:px-6 sm:py-4 [--animation-delay:1200ms]"
        >
          <CardContent variant="custom" padding="none" className="flex w-full flex-col items-start gap-4">
            <p className="type-body-m text-foundation-base-darkbase-dark-500 text-sm tracking-normal leading-5 sm:text-[length:var(--body-m-font-size)] sm:tracking-[var(--body-m-letter-spacing)] sm:leading-[var(--body-m-line-height)]">
              For inquiries unrelated to emergency services, email{" "}
              <span className="font-semibold text-sm tracking-normal leading-5 sm:text-[length:var(--body-m-semibold-font-size)] sm:tracking-[var(--body-m-semibold-letter-spacing)] sm:leading-[var(--body-m-semibold-line-height)] [font-style:var(--body-m-semibold-font-style)]">
                <a
                  href={`mailto:${supportEmail}`}
                  className="underline-offset-2 hover:underline"
                  onClick={() => {
                    track("button_click", {
                      action: "support_cta_click",
                      control_id: "help_faq_support_email",
                      support_target: "email",
                      support_surface: "help_faq_page",
                      destination: `mailto:${supportEmail}`,
                    });
                  }}
                >
                  {supportEmail}
                </a>
              </span>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
    )
  );
};
